import React, { useState, useEffect, useCallback } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import Switch from "@material-ui/core/Switch";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import AddIcon from "@material-ui/icons/Add";
import SearchIcon from "@material-ui/icons/Search";
import DialpadIcon from "@material-ui/icons/DialpadOutlined";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import DeleteIcon from "@material-ui/icons/DeleteOutline";
import VpnKeyIcon from "@material-ui/icons/VpnKeyOutlined";
import RefreshIcon from "@material-ui/icons/Refresh";
import CallIcon from "@material-ui/icons/Call";
import SvgIcon from "@material-ui/core/SvgIcon";
import AppleIcon from "@material-ui/icons/Apple";
import AndroidIcon from "@material-ui/icons/Android";
import clsx from "clsx";

import api from "../../services/api";
import { useMenuStyles } from "../../components/menuStyles";
import toastError from "../../errors/toastError";
import { useProject } from "../../context/Project/ProjectContext";
import { useWebPhone } from "../../context/WebPhone/WebPhoneContext";
import { useConfirm } from "../../components/ConfirmDialog";
import CredentialsDialog from "./CredentialsDialog";

// ícone de QR code (o MUI v4 não tem um): 3 marcadores de canto + módulos de dados
const QrIcon = props => (
  <SvgIcon {...props} viewBox="0 0 24 24">
    <path fillRule="evenodd" d="M3 3h8v8H3V3zm2 2v4h4V5H5z" />
    <path d="M6 6h2v2H6z" />
    <path fillRule="evenodd" d="M13 3h8v8h-8V3zm2 2v4h4V5h-4z" />
    <path d="M16 6h2v2h-2z" />
    <path fillRule="evenodd" d="M3 13h8v8H3v-8zm2 2v4h4v-4H5z" />
    <path d="M6 16h2v2H6z" />
    <path d="M13 13h2v2h-2zM17 13h2v2h-2zM15 15h2v2h-2zM13 17h2v2h-2zM19 17h2v2h-2zM17 19h2v2h-2z" />
  </SvgIcon>
);

const useStyles = makeStyles(theme => ({
  page: { height: "100%", overflowY: "auto", background: theme.app.chatBg },
  wrap: { width: "100%", maxWidth: 1040, margin: "0 auto", padding: theme.spacing(4, 3, 6) },
  header: { display: "flex", alignItems: "flex-start", gap: 16, marginBottom: theme.spacing(0.5) },
  title: { fontFamily: theme.app.heading, fontWeight: 700, fontSize: 22, color: theme.app.ink },
  subtitle: { color: theme.app.textSecondary, fontSize: 13.5, marginTop: 4, maxWidth: 560 },
  grow: { flexGrow: 1 },
  primaryBtn: {
    flexShrink: 0,
    textTransform: "none",
    fontSize: 13,
    fontWeight: 600,
    color: "#fff",
    background: theme.app.dark,
    borderRadius: 8,
    padding: "9px 18px",
    boxShadow: "none",
    "&:hover": { background: theme.app.ink, boxShadow: "none" },
  },

  // prompt simples de download do app (Numera Fone)
  appPrompt: { display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", margin: theme.spacing(2.5, 0), fontSize: 13.5, color: theme.app.textTertiary },
  appLink: { display: "inline-flex", alignItems: "center", gap: 6, color: theme.app.ink, fontWeight: 600, textDecoration: "none", cursor: "pointer", "& svg": { fontSize: 18 }, "&:hover": { color: theme.app.green } },
  appDot: { color: theme.app.textTertiary },

  // busca + pills de filtro
  search: { width: "100%", marginBottom: theme.spacing(2) },
  searchInput: { borderRadius: 999, background: "#fff" },
  pills: { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: theme.spacing(2) },
  pill: {
    display: "inline-flex", alignItems: "center", gap: 6, height: 30, padding: "0 14px",
    borderRadius: 999, border: `1px solid ${theme.app.border}`, background: "#fff",
    color: theme.app.textSecondary, fontSize: 12.5, fontWeight: 600, cursor: "pointer", userSelect: "none",
    "&:hover": { background: theme.app.hover },
  },
  pillOn: { background: theme.app.green, borderColor: theme.app.green, color: "#fff", "&:hover": { background: theme.app.green } },
  pillCount: { fontSize: 11, fontWeight: 500, opacity: 0.8 },

  // lista de cards
  list: { display: "flex", flexDirection: "column", gap: 10 },
  extCard: {
    display: "flex", alignItems: "center", gap: 16, background: "#fff",
    border: `1px solid ${theme.app.border}`, borderRadius: 16, padding: "12px 18px",
    transition: "box-shadow .15s ease, border-color .15s ease",
    "&:hover": { boxShadow: "0 4px 16px rgba(27,34,42,.06)", borderColor: theme.app.borderInput },
    "&:hover $kebab": { opacity: 1 },
  },
  avatar: {
    width: 42, height: 42, borderRadius: 999, flexShrink: 0, background: "#EFF8D6", color: "#5f7a12",
    display: "flex", alignItems: "center", justifyContent: "center", "& svg": { fontSize: 21 },
  },
  idCol: { width: 220, flexShrink: 0, minWidth: 0 },
  sip: { fontFamily: theme.app.mono, fontSize: 14.5, fontWeight: 600, color: theme.app.ink },
  extName: { fontSize: 12.5, color: theme.app.textTertiary, marginTop: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  infoLabel: { fontSize: 10, textTransform: "uppercase", letterSpacing: ".05em", color: theme.app.textTertiary, fontWeight: 600, marginBottom: 3 },
  regCol: { flex: 1, minWidth: 120 },
  statusLine: { display: "inline-flex", alignItems: "center", gap: 7, fontSize: 13, color: theme.app.ink },
  dot: { width: 7, height: 7, borderRadius: "50%", flexShrink: 0 },
  dotOn: { background: theme.app.ok },
  dotOff: { background: theme.app.textTertiary },
  switchCol: { width: 74, flexShrink: 0, textAlign: "center" },
  actions: { display: "flex", alignItems: "center", gap: 4, flexShrink: 0, marginLeft: "auto" },
  actBtn: {
    color: theme.app.textSecondary,
    "&:hover": { color: theme.app.ink, background: theme.app.hover },
    "&.Mui-disabled": { color: theme.app.border },
  },
  kebab: { color: theme.app.textTertiary, opacity: 0.55, transition: "opacity .12s", "&:hover": { color: theme.app.ink } },
  menuPaper: { minWidth: 190, borderRadius: 16, border: `1px solid ${theme.app.border}`, boxShadow: "0 12px 30px rgba(27,34,42,.14)", marginTop: 6 },
  menuItem: { fontSize: 13, gap: 10, padding: "8px 12px", "& svg": { fontSize: 18, color: theme.app.textTertiary } },
  menuDanger: { color: theme.app.danger || "#C0453B", "& svg": { color: theme.app.danger || "#C0453B" } },

  // empty state (card centralizado)
  empty: { background: "#fff", border: `1px solid ${theme.app.border}`, borderRadius: 16, padding: theme.spacing(7, 3), textAlign: "center" },
  emptyIcon: { width: 46, height: 46, borderRadius: 999, background: theme.app.lineSoft, display: "inline-flex", alignItems: "center", justifyContent: "center", marginBottom: 12, "& svg": { fontSize: 22, color: theme.app.textTertiary } },
  emptyText: { color: theme.app.textSecondary, fontSize: 13.5 },
}));

const Extensions = () => {
  const { confirm } = useConfirm();
  const classes = useStyles();
  const estilos = useMenuStyles();
  const { activeProjectId } = useProject();
  const webphone = useWebPhone();
  const [extensions, setExtensions] = useState([]);
  const [realm, setRealm] = useState(null);
  const [sipWss, setSipWss] = useState(null);
  const [form, setForm] = useState(null);
  const [creds, setCreds] = useState(null);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all"); // all | registered | offline
  const [menu, setMenu] = useState(null); // { anchor, ext }

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/extensions", { params: activeProjectId ? { projectId: activeProjectId } : {} });
      setExtensions(data.extensions || []);
      setRealm(data.realm || null);
      setSipWss(data.sipWss || null);
    } catch (err) {
      toastError(err);
    }
  }, [activeProjectId]);

  useEffect(() => {
    load();
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, [load]);

  const openNew = () => setForm({ name: "", sipUsername: "" });

  const create = async () => {
    if (!form.name.trim() || !form.sipUsername.trim() || saving) return;
    setSaving(true);
    try {
      const { data } = await api.post("/extensions", {
        name: form.name.trim(),
        sipUsername: form.sipUsername.trim(),
        projectId: activeProjectId || undefined,
      });
      setForm(null);
      await load();
      setCreds(data);
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async ext => {
    try {
      await api.put(`/extensions/${ext.id}`, { isActive: !ext.isActive });
      load();
    } catch (err) {
      toastError(err);
    }
  };

  const regenerate = async ext => {
    setMenu(null);
    if (!(await confirm({ title: "Nova senha", message: `Gerar uma nova senha para o ramal ${ext.sipUsername}? A senha atual deixará de funcionar.`, confirmText: "Gerar", danger: true }))) return;
    try {
      const { data } = await api.put(`/extensions/${ext.id}`, { regenerateSecret: true });
      await load();
      setCreds(data);
    } catch (err) {
      toastError(err);
    }
  };

  const remove = async ext => {
    setMenu(null);
    if (!(await confirm({ title: "Remover ramal", message: `Remover o ramal ${ext.sipUsername} (${ext.name})?`, confirmText: "Remover", danger: true }))) return;
    try {
      await api.delete(`/extensions/${ext.id}`);
      load();
    } catch (err) {
      toastError(err);
    }
  };

  // filtros: busca (usuário/nome) + status de registro
  const q = search.trim().toLowerCase();
  const bySearch = extensions.filter(e =>
    !q || `${e.sipUsername || ""} ${e.name || ""}`.toLowerCase().includes(q)
  );
  const counts = {
    all: bySearch.length,
    registered: bySearch.filter(e => e.registered).length,
    offline: bySearch.filter(e => !e.registered).length,
  };
  const filtered = bySearch.filter(e => {
    if (filter === "registered") return !!e.registered;
    if (filter === "offline") return !e.registered;
    return true;
  });

  const pill = (key, label) => (
    <div className={clsx(classes.pill, filter === key && classes.pillOn)} onClick={() => setFilter(key)}>
      {label}
      <span className={classes.pillCount}>{counts[key]}</span>
    </div>
  );

  return (
    <div className={classes.page}>
      <div className={classes.wrap}>
        <div className={classes.header}>
          <div>
            <Typography className={classes.title}>Ramais</Typography>
            <Typography className={classes.subtitle}>
              Ramais SIP do PABX. Use as credenciais num softphone (Zoiper, Linphone) ou no webphone.
            </Typography>
          </div>
          <div className={classes.grow} />
          <Button className={classes.primaryBtn} startIcon={<AddIcon />} onClick={openNew}>
            Novo ramal
          </Button>
        </div>

        <div className={classes.appPrompt}>
          Ainda não tem o app?
          <span className={classes.appLink}>
            <AppleIcon /> iPhone
          </span>
          <span className={classes.appDot}>·</span>
          <span className={classes.appLink}>
            <AndroidIcon /> Android
          </span>
        </div>

        <TextField
          className={classes.search}
          placeholder="Buscar por usuário ou nome…"
          variant="outlined"
          size="small"
          value={search}
          onChange={e => setSearch(e.target.value)}
          InputProps={{
            className: classes.searchInput,
            startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
          }}
        />

        <div className={classes.pills}>
          {pill("all", "Todos")}
          {pill("registered", "Registrados")}
          {pill("offline", "Offline")}
        </div>

        {filtered.length === 0 ? (
          <div className={classes.empty}>
            <div className={classes.emptyIcon}><DialpadIcon /></div>
            <div className={classes.emptyText}>
              {extensions.length === 0 ? 'Nenhum ramal ainda. Clique em "Novo ramal" para criar o primeiro.' : "Nenhum ramal com esse filtro."}
            </div>
          </div>
        ) : (
          <div className={classes.list}>
            {filtered.map(ext => (
              <div key={ext.id} className={classes.extCard}>
                <div className={classes.avatar}><DialpadIcon /></div>

                <div className={classes.idCol}>
                  <div className={classes.sip}>{ext.sipUsername}</div>
                  {ext.name && <div className={classes.extName}>{ext.name}</div>}
                </div>

                <div className={classes.regCol}>
                  <div className={classes.infoLabel}>Registro</div>
                  <span className={classes.statusLine}>
                    <span className={clsx(classes.dot, ext.registered ? classes.dotOn : classes.dotOff)} />
                    {ext.registered ? "Registrado" : "Offline"}
                  </span>
                </div>

                <div className={classes.switchCol}>
                  <div className={classes.infoLabel}>Ativo</div>
                  <Switch checked={!!ext.isActive} onChange={() => toggleActive(ext)} color="primary" size="small" />
                </div>

                <div className={classes.actions}>
                  <Tooltip title="Conectar">
                    <span>
                      <IconButton
                        size="small"
                        className={classes.actBtn}
                        onClick={() => webphone && webphone.openWebphone(ext, realm, sipWss)}
                        disabled={!sipWss || !webphone}
                      >
                        <CallIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Credenciais do ramal (QR)">
                    <IconButton size="small" className={classes.actBtn} onClick={() => setCreds(ext)}>
                      <QrIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <IconButton size="small" className={classes.kebab} onClick={e => setMenu({ anchor: e.currentTarget, ext })}>
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Menu
        anchorEl={menu && menu.anchor}
        open={!!menu}
        onClose={() => setMenu(null)}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{ className: classes.menuPaper }}
      >
        <MenuItem className={classes.menuItem} onClick={() => { const e = menu.ext; setMenu(null); setCreds(e); }}>
          <VpnKeyIcon fontSize="small" />
          Ver credenciais
        </MenuItem>
        <MenuItem className={classes.menuItem} onClick={() => regenerate(menu.ext)}>
          <RefreshIcon fontSize="small" />
          Gerar nova senha
        </MenuItem>
        <MenuItem className={clsx(classes.menuItem, classes.menuDanger)} onClick={() => remove(menu.ext)}>
          <DeleteIcon fontSize="small" />
          Remover ramal
        </MenuItem>
      </Menu>

      {/* create dialog */}
      <Dialog
        open={!!form}
        onClose={() => setForm(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ className: estilos.dialogPaper }}
      >
        <DialogTitle>Novo ramal</DialogTitle>
        {form && (
          <form onSubmit={e => { e.preventDefault(); create(); }}>
            <DialogContent>
              <TextField
                autoFocus fullWidth variant="outlined" size="small" margin="dense"
                label="Nome" placeholder="Ex.: João — Vendas"
                value={form.name}
                onChange={e => { const v = e.target.value; setForm(f => ({ ...f, name: v })); }}
              />
              <TextField
                fullWidth variant="outlined" size="small" margin="dense"
                label="Usuário SIP" placeholder="Ex.: 1001"
                value={form.sipUsername}
                onChange={e => { const v = e.target.value; setForm(f => ({ ...f, sipUsername: v })); }}
                helperText="Identificador do ramal. A senha é gerada automaticamente."
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setForm(null)} disabled={saving}>Cancelar</Button>
              <Button type="submit" color="primary" variant="contained" disabled={!form.name.trim() || !form.sipUsername.trim() || saving}>
                Criar
              </Button>
            </DialogActions>
          </form>
        )}
      </Dialog>

      <CredentialsDialog
        open={!!creds}
        creds={creds}
        realm={realm}
        onClose={() => setCreds(null)}
        onRegenerate={() => regenerate(creds)}
      />
    </div>
  );
};

export default Extensions;
